import React, { MouseEvent, useEffect, useMemo } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { getStringsFirstLetter, isObjectEmpty } from "../../../../utils/commonUtils";
import palette, { MESSAGES_LOGO_COLORS, AVATAR_TEXT_COLORS } from "../../../../theme/palette";
import { IChat, IChatsRecord } from "../../../../interfaces/chats";
import { EventTypeFilterEnum } from "../../../../constants/enums";
import youCaterAvatar from "../../../../assets/images/youCaterAvatar.png";
import { formatMessageTimestamp } from "../../../../utils/formatTime";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "../../../../routes";
import { useAppDispatch, useAppSelector } from "../../../../hooks/storeHooks";
import { createAdminVendorChat, getAdminVendorChatRecordThunk } from "../../../../store/chats/chatsThunk";
import { REDUX_REQUEST_STATUS } from "../../../../types/commonTypes";
import { useToast } from "../../../../context/ToasterContext";
import STATUS_CODES from "../../../../constants/statusCodesConstants";
import MentionBadge from "../../../SendBird/MentionBadge/MentionBadge";
import { shouldShowMentionBadge } from "../../../../utils/sendbird/chatAccessibility";
import { ChatMenuAnchor } from "../ChatContextMenu/ChatContextMenu";
import LineDotsSvg from "../../../../assets/svg-fc/LineDotsSvg";
import "./chatItem.scss";

interface ChatItemProps {
	chat: IChat;
	event: IChatsRecord;
	isSelected: boolean;
	index: number;
	isLastChat?: boolean;
	sdk: any;
	chatIndex?: number;
	onChatClick: (eventId: string, chatId: string) => void;
	isMenuOpen?: boolean;
	onOpenMenu?: (anchor: ChatMenuAnchor) => void;
}

const ChatMoreButton = ({
	label,
	isMenuOpen,
	onOpenMenu,
}: {
	label: string;
	isMenuOpen?: boolean;
	onOpenMenu: (anchor: ChatMenuAnchor) => void;
}) => (
	<Box
		component="button"
		type="button"
		aria-label={`More actions for ${label}`}
		aria-haspopup="menu"
		aria-expanded={Boolean(isMenuOpen)}
		className="chatMoreButton"
		onClick={(clickEvent: MouseEvent<HTMLElement>) => {
			clickEvent.stopPropagation();
			onOpenMenu({ element: clickEvent.currentTarget });
		}}
	>
		<LineDotsSvg size={18} />
	</Box>
);

const contextMenuHandler = (onOpenMenu?: (anchor: ChatMenuAnchor) => void) =>
	onOpenMenu
		? (contextEvent: MouseEvent) => {
				contextEvent.preventDefault();
				onOpenMenu({ x: contextEvent.clientX, y: contextEvent.clientY });
			}
		: undefined;

const bgColors = Object.values(MESSAGES_LOGO_COLORS);

const ChatItem: React.FC<ChatItemProps> = ({
	chat,
	event,
	isSelected,
	onChatClick,
	index,
	isLastChat,
	sdk,
	chatIndex = 0,
	isMenuOpen,
	onOpenMenu,
}) => {
	const eventType = event.eventType;

	const getAvatarBgColor = (index: number): string => {
		return bgColors[index % bgColors.length];
	};

	const getAvatarTextColor = (bgColor: string): string => {
		return AVATAR_TEXT_COLORS[bgColor] || palette.grey["1100"];
	};

	const avatarBg = getAvatarBgColor(chatIndex);
	const avatarTextColor = getAvatarTextColor(avatarBg);

	const customerDetails = useMemo(() => {
		if (eventType === EventTypeFilterEnum.B2C || eventType === EventTypeFilterEnum.Prepaid) {
			if (isObjectEmpty(event))
				return {
					customerFirstLastName: "",
					eventName: "",
					eventDate: "",
					customerName: "",
				};
			return {
				customerFirstLastName: getStringsFirstLetter(
					event.customerName?.split(" ")?.[0],
					event.customerName?.split(" ")?.[1],
				),
				eventName: event.eventName,
				eventDate: event.eventDate,
				customerName: event.customerName,
			};
		}
	}, [event, eventType]);

	const handleActivate = () => onChatClick(event.eventId, chat.id);
	const chatName =
		eventType === EventTypeFilterEnum.B2B ? "YouCater Corporate Requests" : customerDetails?.customerName || "";

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handleActivate();
		}
	};

	return (
		<Box
			className={`chatItem ${isSelected ? "selected" : ""} ${isLastChat ? "lastChat" : ""} ${isMenuOpen ? "menuOpen" : ""}`}
			role="button"
			tabIndex={0}
			onClick={handleActivate}
			onKeyDown={handleKeyDown}
			onContextMenu={contextMenuHandler(onOpenMenu)}
		>
			<Box className="chatItemContent">
				{eventType === EventTypeFilterEnum.B2B ? (
					<Avatar className="avatarBackground" src={youCaterAvatar} />
				) : (
					<Box className="avatarBackground" sx={{ backgroundColor: avatarBg }}>
						<Typography className="avatarText" sx={{ color: avatarTextColor }}>
							{customerDetails?.customerFirstLastName || ""}
						</Typography>
					</Box>
				)}
				<Box className="chatDetails">
					<Box className="chatHeaderRow">
						<Typography className="vendorName">{chatName}</Typography>
						{chat?.lastMessageAt && (
							<Typography className="messageTime">
								{chat.lastMessageAt ? formatMessageTimestamp(new Date(chat.lastMessageAt).toString()) : ""}
							</Typography>
						)}
					</Box>
					<Box className="chatMessageRow">
						<Typography className="lastMessage">{chat?.lastMessage || "No messages yet"}</Typography>
						<Box className="chatRowBadges">
							{shouldShowMentionBadge(chat) ? <MentionBadge /> : null}
							{chat.unreadMessageCount && chat.unreadMessageCount > 0 ? (
								<Box className="unreadBadge">
									<Typography>{chat.unreadMessageCount}</Typography>
								</Box>
							) : null}
							{onOpenMenu ? (
								<ChatMoreButton label={chatName} isMenuOpen={isMenuOpen} onOpenMenu={onOpenMenu} />
							) : null}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export const AdminVendorChatItem = ({
	handleSelect,
	isMenuOpen,
	onOpenMenu,
}: {
	handleSelect: () => void;
	isMenuOpen?: boolean;
	onOpenMenu?: (anchor: ChatMenuAnchor) => void;
}) => {
	const { showToast } = useToast();
	const navigate = useNavigate();
	const location = useLocation();
	const pathName = location.pathname;
	const dispatch = useAppDispatch();
	const adminVendorChatRecord = useAppSelector((state) => state.chats.adminVendorChatRecord);
	const userDetails = useAppSelector((state) => state.user.userDetails);

	const isSelected = pathName === ROUTE_PATHS.ADMIN_VENDOR_CHAT;

	const handleCreateChannel = async (vendorId: string) => {
		try {
			const res = await dispatch(createAdminVendorChat({ vendorId }));

			if (res.meta.requestStatus !== REDUX_REQUEST_STATUS.FULFILLED) {
				throw new Error("Create channel failed");
			}

			await getAdminVendorChat(true);
		} catch {
			showToast("Something went wrong", () => {}, "error");
		}
	};

	const getAdminVendorChat = async (initiatedAgain = false) => {
		try {
			const res = await dispatch(getAdminVendorChatRecordThunk());

			if (res.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				navigate(`${ROUTE_PATHS.ADMIN_VENDOR_CHAT}${location.search}`);
				return;
			}

			// If already retried, stop here
			if (initiatedAgain) return;

			const payload = res?.payload as {
				statusCode: number;
				message: string;
			};

			// Status when vendor chat not created yet
			if (payload?.statusCode === STATUS_CODES.ADMIN_VENDOR_CHAT.ADMIN_VENDOR_CHAT_DO_NOT_EXIST) {
				await handleCreateChannel(userDetails?.vendorId as string);
			} else {
				throw new Error("Fetch chat failed");
			}
		} catch {}
	};

	useEffect(() => {
		dispatch(getAdminVendorChatRecordThunk());
	}, [dispatch]);

	const handleAdminActivate = () => {
		if (!isSelected) {
			handleSelect();
			getAdminVendorChat();
		}
	};

	const handleAdminKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handleAdminActivate();
		}
	};

	return (
		<Box
			className={`chatItem adminVendorChatItem ${isMenuOpen ? "menuOpen" : ""}`}
			role="button"
			tabIndex={0}
			onClick={handleAdminActivate}
			onKeyDown={handleAdminKeyDown}
			onContextMenu={contextMenuHandler(onOpenMenu)}
		>
			<Box className="chatItemContent">
				<Avatar className="avatarBackground" src={youCaterAvatar} />

				<Box className="chatDetails adminVendorChatDetails">
					<Box className="chatHeaderRow">
						<Typography className="vendorName adminVendorChatVendorName">YouCater Admin</Typography>
						{adminVendorChatRecord?.lastMessageAt && (
							<Typography className="messageTime">
								{adminVendorChatRecord.lastMessageAt
									? formatMessageTimestamp(new Date(adminVendorChatRecord.lastMessageAt).toString())
									: ""}
							</Typography>
						)}
					</Box>
					<Box className="chatMessageRow">
						<Typography className="lastMessage adminVendorChatLastMessage">
							{adminVendorChatRecord?.lastMessage || "Chat with YouCater Admin"}
						</Typography>
						<Box className="chatRowBadges">
							{shouldShowMentionBadge(adminVendorChatRecord ?? {}) ? <MentionBadge /> : null}
							{adminVendorChatRecord?.unreadMessageCount && adminVendorChatRecord?.unreadMessageCount > 0 ? (
								<Box className="unreadBadge">
									<Typography>{adminVendorChatRecord.unreadMessageCount}</Typography>
								</Box>
							) : null}
							{onOpenMenu ? (
								<ChatMoreButton label="YouCater Admin" isMenuOpen={isMenuOpen} onOpenMenu={onOpenMenu} />
							) : null}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default ChatItem;
